import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { api } from '../api'
import type { AppSettings, Category, Client, Todo } from '../../shared/types'
import { formatHours, parseDuration } from '../../shared/duration'
import { appToday, mmddyy } from '../datetime'
import { pushError } from '../toast'
import CategoryCombo from './CategoryCombo'
import ClientCombo from './ClientCombo'
import LogTodoModal, { type LogEntryPatch } from './LogTodoModal'

type CalMode = 'day' | 'week' | 'month'
const PX_PER_MIN = 1

function shift(iso: string, n: number): string {
  const d = new Date(`${iso}T00:00:00Z`)
  d.setUTCDate(d.getUTCDate() + n)
  return d.toISOString().slice(0, 10)
}
function mondayOf(iso: string): string {
  const d = new Date(`${iso}T00:00:00Z`)
  const dow = d.getUTCDay()
  return shift(iso, dow === 0 ? -6 : 1 - dow)
}
function monthFirst(iso: string): string {
  return iso.slice(0, 8) + '01'
}
function hhmmToMin(v: string): number {
  const [h, m] = v.split(':').map(Number)
  return h * 60 + (m || 0)
}
function minToHHMM(min: number): string {
  const m = ((Math.round(min) % 1440) + 1440) % 1440
  return `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`
}
function minToLabel(min: number): string {
  const h = Math.floor(min / 60)
  const mm = min % 60
  const ap = h < 12 ? 'AM' : 'PM'
  const h12 = h % 12 === 0 ? 12 : h % 12
  return mm === 0 ? `${h12} ${ap}` : `${h12}:${String(mm).padStart(2, '0')} ${ap}`
}
function dowShort(iso: string): string {
  return new Date(`${iso}T00:00:00Z`).toLocaleDateString('en-US', { weekday: 'short', timeZone: 'UTC' })
}
function dayNum(iso: string): number {
  return Number(iso.slice(8, 10))
}
function withAlpha(hex: string, a: string): string {
  return /^#[0-9a-fA-F]{6}$/.test(hex) ? hex + a : hex
}

interface CalTodo {
  todo: Todo
  startMin: number
  endMin: number
  col: number
  cols: number
  placed: boolean // has a real due_date (vs. auto-placed on today)
}
function layoutDay(items: CalTodo[]): CalTodo[] {
  const sorted = [...items].sort((a, b) => a.startMin - b.startMin || a.endMin - b.endMin)
  const out: CalTodo[] = []
  let cluster: CalTodo[] = []
  let end = -1
  const flush = () => {
    if (!cluster.length) return
    const colEnds: number[] = []
    for (const it of cluster) {
      let p = colEnds.findIndex((e) => it.startMin >= e)
      if (p === -1) {
        p = colEnds.length
        colEnds.push(it.endMin)
      } else colEnds[p] = it.endMin
      it.col = p
    }
    const cols = colEnds.length
    for (const it of cluster) out.push({ ...it, cols })
    cluster = []
    end = -1
  }
  for (const it of sorted) {
    if (cluster.length && it.startMin >= end) flush()
    cluster.push(it)
    end = Math.max(end, it.endMin)
  }
  flush()
  return out
}

export default function TodoCalendar({ filterClient, onChanged }: { filterClient: number | 'all'; onChanged?: () => void }) {
  const [mode, setMode] = useState<CalMode>('week')
  const [anchor, setAnchor] = useState(appToday())
  const [settings, setSettings] = useState<AppSettings | null>(null)
  const [clients, setClients] = useState<Client[]>([])
  const [allCats, setAllCats] = useState<Category[]>([])
  const [todos, setTodos] = useState<Todo[]>([])
  const [sel, setSel] = useState<number | null>(null)
  const [logging, setLogging] = useState<Todo | null>(null)
  const [ready, setReady] = useState(false)

  useEffect(() => {
    ;(async () => {
      try {
        const [st, cl, cats, td] = await Promise.all([api.getSettings(), api.listClients(false), api.listAllCategories(), api.listTodos()])
        setSettings(st)
        setClients(cl)
        setAllCats(cats)
        setTodos(td)
      } catch (e) {
        pushError(`Couldn't load calendar: ${(e as Error).message}`)
      } finally {
        setReady(true)
      }
    })()
  }, [])

  const dailyStart = settings ? hhmmToMin(settings.daily_start_time ?? '08:00') : 480
  const interval = settings?.daily_interval_minutes ?? 15
  const clientById = useMemo(() => new Map(clients.map((c) => [c.id, c])), [clients])

  const days = useMemo(() => {
    if (mode === 'day') return [anchor]
    if (mode === 'week') {
      const mon = mondayOf(anchor)
      return Array.from({ length: 7 }, (_, i) => shift(mon, i))
    }
    // month grid: full weeks covering the month
    const first = monthFirst(anchor)
    const gridStart = mondayOf(first)
    return Array.from({ length: 42 }, (_, i) => shift(gridStart, i))
  }, [mode, anchor])

  // Todos with a due date show on that day; undated todos auto-show on today.
  const visibleTodos = useMemo(() => {
    const set = new Set(days)
    const todayShown = set.has(appToday())
    return todos.filter((t) => {
      if (t.done) return false
      if (filterClient !== 'all' && t.client_id !== filterClient) return false
      if (t.due_date && set.has(t.due_date)) return true
      if (t.due_date == null && todayShown) return true
      return false
    })
  }, [todos, days, filterClient])

  const patch = useCallback(
    (id: number, p: Partial<Todo>) => {
      setTodos((ts) => ts.map((t) => (t.id === id ? { ...t, ...p } : t)))
      api
        .updateTodo(id, p)
        .then(() => onChanged?.())
        .catch((e) => pushError(`Couldn't save: ${(e as Error).message}`))
    },
    [onChanged],
  )
  const createAt = useCallback(
    async (due_date: string, start_time: string | null, est_minutes: number | null) => {
      try {
        const t = await api.createTodo({
          client_id: filterClient === 'all' ? null : filterClient,
          task: '',
          due_date,
          start_time,
          est_minutes,
        })
        setTodos((ts) => [...ts, t])
        setSel(t.id)
        onChanged?.()
      } catch (e) {
        pushError(`Couldn't create: ${(e as Error).message}`)
      }
    },
    [filterClient, onChanged],
  )
  const remove = useCallback(
    async (id: number) => {
      setTodos((ts) => ts.filter((t) => t.id !== id))
      setSel(null)
      try {
        await api.deleteTodo(id)
        onChanged?.()
      } catch (e) {
        pushError(`Delete failed: ${(e as Error).message}`)
      }
    },
    [onChanged],
  )

  async function completeTodo(t: Todo, logEntry: boolean, p?: LogEntryPatch) {
    try {
      if (logEntry && p && p.client_id != null) {
        const body: Record<string, unknown> = {
          client_id: p.client_id,
          entry_date: p.entry_date,
          description: p.description,
          category_id: p.category_id,
          duration_minutes: p.duration_minutes,
        }
        if (p.start_time) {
          body.start_time = p.start_time
          body.end_time = minToHHMM(hhmmToMin(p.start_time) + p.duration_minutes)
        }
        await api.createEntry(body as any)
      }
      await api.updateTodo(t.id, { done: 1 })
      setTodos((ts) => ts.filter((x) => x.id !== t.id))
      setSel(null)
      setLogging(null)
      onChanged?.()
    } catch (e) {
      pushError(`Couldn't complete: ${(e as Error).message}`)
    }
  }

  function navHeaderLabel(): string {
    if (mode === 'day') return new Date(`${anchor}T00:00:00Z`).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric', timeZone: 'UTC' })
    if (mode === 'week') {
      const mon = mondayOf(anchor)
      return `${mmddyy(mon)} – ${mmddyy(shift(mon, 6))}`
    }
    return new Date(`${anchor}T00:00:00Z`).toLocaleDateString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' })
  }
  function step(dir: number) {
    if (mode === 'day') setAnchor((a) => shift(a, dir))
    else if (mode === 'week') setAnchor((a) => shift(a, dir * 7))
    else
      setAnchor((a) => {
        const [y, m] = a.split('-').map(Number)
        return new Date(Date.UTC(y, m - 1 + dir, 1)).toISOString().slice(0, 10)
      })
  }

  if (!ready || !settings) return <div className="p-10 text-slate-400">Loading…</div>

  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-1">
          <button className="rounded px-2 py-1 text-slate-500 hover:bg-slate-100" onClick={() => step(-1)} aria-label="Previous">
            ‹
          </button>
          <button className="rounded px-2 py-1 text-sm text-[var(--accent)] hover:underline" onClick={() => setAnchor(appToday())}>
            Today
          </button>
          <button className="rounded px-2 py-1 text-slate-500 hover:bg-slate-100" onClick={() => step(1)} aria-label="Next">
            ›
          </button>
          <span className="ml-2 text-sm font-medium text-slate-700">{navHeaderLabel()}</span>
        </div>
        <div className="flex overflow-hidden rounded-md border border-slate-300 text-sm">
          {(['day', 'week', 'month'] as CalMode[]).map((m) => (
            <button
              key={m}
              className={'px-3 py-1 capitalize ' + (mode === m ? 'bg-slate-900 text-white' : 'bg-white text-slate-600 hover:bg-slate-100')}
              onClick={() => setMode(m)}
            >
              {m}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_20rem]">
        <div>
          {mode === 'month' ? (
            <MonthGrid days={days} anchorMonth={anchor.slice(0, 7)} todos={visibleTodos} clientById={clientById} selId={sel} onSelect={setSel} onMoveDay={(id, d) => patch(id, { due_date: d })} onCreate={(d) => createAt(d, null, null)} />
          ) : (
            <TimeGrid
              days={days}
              dailyStart={dailyStart}
              interval={interval}
              todos={visibleTodos}
              clientById={clientById}
              allCats={allCats}
              selId={sel}
              onSelect={setSel}
              onCreate={(d, startMin, dur) => createAt(d, minToHHMM(startMin), dur)}
              onMove={(id, d, startMin) => patch(id, { due_date: d, start_time: minToHHMM(startMin) })}
            />
          )}
        </div>
        <aside>
          <TodoSidePanel
            sel={sel}
            todos={todos}
            clients={clients}
            allCats={allCats}
            dailyStart={dailyStart}
            onPatch={patch}
            onDelete={remove}
            onLog={(t) => setLogging(t)}
            onClose={() => setSel(null)}
          />
        </aside>
      </div>

      {logging && (
        <LogTodoModal
          todo={logging}
          clients={clients}
          allCats={allCats}
          onCancel={() => setLogging(null)}
          onLog={(p) => completeTodo(logging, true, p)}
          onDontLog={() => completeTodo(logging, false)}
        />
      )}
    </div>
  )
}

// ── Day / Week time grid ─────────────────────────────────────────────────────
function TimeGrid({
  days,
  dailyStart,
  interval,
  todos,
  clientById,
  allCats,
  selId,
  onSelect,
  onCreate,
  onMove,
}: {
  days: string[]
  dailyStart: number
  interval: number
  todos: Todo[]
  clientById: Map<number, Client>
  allCats: Category[]
  selId: number | null
  onSelect: (id: number) => void
  onCreate: (day: string, startMin: number, durMin: number) => void
  onMove: (id: number, day: string, startMin: number) => void
}) {
  const today = appToday()
  const [viewStart, setViewStart] = useState(dailyStart)
  const [viewEnd, setViewEnd] = useState(dailyStart + 8 * 60)
  const [drag, setDrag] = useState<{ dayIdx: number; a: number; b: number } | null>(null)
  const [move, setMove] = useState<{ id: number; dayIdx: number; startMin: number; dur: number } | null>(null)
  const colsRef = useRef<HTMLDivElement>(null)

  const height = (viewEnd - viewStart) * PX_PER_MIN
  const snap = (m: number) => Math.round(m / interval) * interval
  const ticks: number[] = []
  for (let t = Math.ceil(viewStart / 60) * 60; t <= viewEnd; t += 60) ticks.push(t)

  function colGeom() {
    const rect = colsRef.current!.getBoundingClientRect()
    return { left: rect.left, top: rect.top, colW: rect.width / days.length }
  }
  function yToMin(clientY: number) {
    const { top } = colGeom()
    return snap(viewStart + (clientY - top) / PX_PER_MIN)
  }

  function startCreate(e: React.MouseEvent, dayIdx: number) {
    if (e.button !== 0) return
    const s = yToMin(e.clientY)
    setDrag({ dayIdx, a: s, b: s + interval })
    const mv = (ev: MouseEvent) => setDrag({ dayIdx, a: s, b: yToMin(ev.clientY) })
    const up = (ev: MouseEvent) => {
      window.removeEventListener('mousemove', mv)
      window.removeEventListener('mouseup', up)
      let a = s
      let b = yToMin(ev.clientY)
      if (b < a) [a, b] = [b, a]
      if (b - a < interval) b = a + interval
      setDrag(null)
      onCreate(days[dayIdx], Math.max(0, a), Math.max(interval, b - a))
    }
    window.addEventListener('mousemove', mv)
    window.addEventListener('mouseup', up)
  }

  function startMove(e: React.MouseEvent, ct: CalTodo) {
    e.stopPropagation()
    const dur = ct.endMin - ct.startMin
    const anchorY = e.clientY
    const origStart = ct.startMin
    let moved = false
    const mv = (ev: MouseEvent) => {
      if (Math.abs(ev.clientY - anchorY) > 3 || Math.abs(ev.clientX - e.clientX) > 3) moved = true
      if (!moved) return
      const { left, colW } = colGeom()
      const di = Math.max(0, Math.min(days.length - 1, Math.floor((ev.clientX - left) / colW)))
      const ns = Math.max(0, Math.min(1440 - dur, snap(origStart + (ev.clientY - anchorY) / PX_PER_MIN)))
      setMove({ id: ct.todo.id, dayIdx: di, startMin: ns, dur })
    }
    const up = () => {
      window.removeEventListener('mousemove', mv)
      window.removeEventListener('mouseup', up)
      setMove((cur) => {
        if (moved && cur) onMove(ct.todo.id, days[cur.dayIdx], cur.startMin)
        else onSelect(ct.todo.id)
        return null
      })
    }
    window.addEventListener('mousemove', mv)
    window.addEventListener('mouseup', up)
  }

  return (
    <div>
      <div className="flex justify-center">
        <button className="text-slate-400 hover:text-slate-700" onClick={() => setViewStart((v) => Math.max(0, v - 180))}>
          ▲ earlier
        </button>
      </div>
      <div className="mt-1 overflow-x-auto rounded-lg border border-slate-200 bg-white">
        <div style={{ minWidth: days.length > 1 ? '46rem' : undefined }}>
        {/* day headers */}
        <div className="flex border-b border-slate-200 bg-slate-50">
          <div className="w-14 shrink-0" />
          {days.map((d) => (
            <div key={d} className="flex-1 border-l border-slate-100 px-2 py-1 text-center text-xs font-semibold text-slate-600">
              {days.length > 1 ? `${dowShort(d)} ${mmddyy(d)}` : dowShort(d)}
            </div>
          ))}
        </div>
        <div className="flex">
          {/* time gutter */}
          <div className="relative w-14 shrink-0 border-r border-slate-100" style={{ height }}>
            {ticks.map((t) => (
              <div key={t} className="absolute right-1 -translate-y-1/2 text-[11px] tabular text-slate-400" style={{ top: (t - viewStart) * PX_PER_MIN }}>
                {minToLabel(t)}
              </div>
            ))}
          </div>
          {/* day columns */}
          <div ref={colsRef} className="flex flex-1">
            {days.map((day, dayIdx) => {
              const items = layoutDay(
                todos
                  .filter((t) => t.due_date === day || (t.due_date == null && day === today))
                  .map((t) => {
                    const s = t.start_time ? hhmmToMin(t.start_time) : dailyStart
                    return { todo: t, startMin: s, endMin: s + (t.est_minutes || 60), col: 0, cols: 1, placed: t.due_date != null }
                  }),
              )
              return (
                <div key={day} className="relative flex-1 cursor-crosshair select-none border-l border-slate-100" style={{ height }} onMouseDown={(e) => startCreate(e, dayIdx)}>
                  {ticks.map((t) => (
                    <div key={t} className="pointer-events-none absolute inset-x-0 border-t border-slate-100" style={{ top: (t - viewStart) * PX_PER_MIN }} />
                  ))}
                  {drag && drag.dayIdx === dayIdx && (
                    <div
                      className="pointer-events-none absolute inset-x-1 rounded border border-[var(--accent)] bg-[var(--accent)]/10"
                      style={{ top: (Math.min(drag.a, drag.b) - viewStart) * PX_PER_MIN, height: Math.max(interval, Math.abs(drag.b - drag.a)) * PX_PER_MIN }}
                    />
                  )}
                  {items.map((ct) => {
                    const cl = ct.todo.client_id != null ? clientById.get(ct.todo.client_id) : undefined
                    const accent = cl?.accent_color ?? '#64748b'
                    const cat = ct.todo.category_id != null ? allCats.find((c) => c.id === ct.todo.category_id) : undefined
                    const moving = move?.id === ct.todo.id
                    const effStart = moving ? move!.startMin : ct.startMin
                    const inThisCol = !moving || move!.dayIdx === dayIdx
                    if (moving && !inThisCol) return null
                    const dur = ct.endMin - ct.startMin
                    return (
                      <button
                        key={ct.todo.id}
                        onMouseDown={(e) => startMove(e, ct)}
                        title={ct.placed ? undefined : 'Unscheduled — drag to a day/time to schedule it'}
                        className={
                          'absolute cursor-move select-none overflow-hidden rounded px-1 py-0.5 text-left text-[11px] leading-tight ' +
                          (selId === ct.todo.id || moving ? 'z-10 ring-2 ring-slate-800 ' : '') +
                          (ct.placed ? 'border' : 'border border-dashed')
                        }
                        style={{
                          top: (effStart - viewStart) * PX_PER_MIN + 1,
                          height: dur * PX_PER_MIN - 2,
                          left: `calc(${(ct.col * 100) / ct.cols}% + 2px)`,
                          width: `calc(${100 / ct.cols}% - 4px)`,
                          backgroundColor: withAlpha(accent, '22'),
                          borderColor: accent,
                          color: '#1f2933',
                        }}
                      >
                        <div className="truncate font-semibold" style={{ color: accent }}>
                          {ct.todo.task || '(untitled)'}
                        </div>
                        <div className="truncate text-slate-500">{cl?.simple_name ?? ''}{cat ? ` · ${cat.name}` : ''}</div>
                      </button>
                    )
                  })}
                </div>
              )
            })}
          </div>
        </div>
        </div>
      </div>
      <div className="flex justify-center">
        <button className="text-slate-400 hover:text-slate-700" onClick={() => setViewEnd((v) => Math.min(1440, v + 180))}>
          ▼ later
        </button>
      </div>
      <p className="mt-1 text-center text-xs text-slate-400">Drag on the grid to add a to-do · drag a block to reschedule.</p>
    </div>
  )
}

// ── Month grid ───────────────────────────────────────────────────────────────
function MonthGrid({
  days,
  anchorMonth,
  todos,
  clientById,
  selId,
  onSelect,
  onMoveDay,
  onCreate,
}: {
  days: string[]
  anchorMonth: string
  todos: Todo[]
  clientById: Map<number, Client>
  selId: number | null
  onSelect: (id: number) => void
  onMoveDay: (id: number, day: string) => void
  onCreate: (day: string) => void
}) {
  const gridRef = useRef<HTMLDivElement>(null)
  const today = appToday()

  function startDrag(e: React.MouseEvent, id: number) {
    e.stopPropagation()
    const anchorX = e.clientX
    const anchorY = e.clientY
    let moved = false
    const mv = (ev: MouseEvent) => {
      if (Math.abs(ev.clientX - anchorX) > 4 || Math.abs(ev.clientY - anchorY) > 4) moved = true
    }
    const up = (ev: MouseEvent) => {
      window.removeEventListener('mousemove', mv)
      window.removeEventListener('mouseup', up)
      if (!moved) {
        onSelect(id)
        return
      }
      const rect = gridRef.current!.getBoundingClientRect()
      const col = Math.max(0, Math.min(6, Math.floor(((ev.clientX - rect.left) / rect.width) * 7)))
      const row = Math.max(0, Math.min(5, Math.floor(((ev.clientY - rect.top) / rect.height) * 6)))
      const target = days[row * 7 + col]
      if (target) onMoveDay(id, target)
    }
    window.addEventListener('mousemove', mv)
    window.addEventListener('mouseup', up)
  }

  return (
    <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
      <div className="grid grid-cols-7 border-b border-slate-200 bg-slate-50 text-center text-xs font-semibold text-slate-500">
        {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((d) => (
          <div key={d} className="py-1">
            {d}
          </div>
        ))}
      </div>
      <div ref={gridRef} className="grid grid-cols-7" style={{ gridTemplateRows: 'repeat(6, minmax(5.5rem, 1fr))' }}>
        {days.map((day) => {
          const inMonth = day.slice(0, 7) === anchorMonth
          const dayTodos = todos.filter((t) => t.due_date === day || (t.due_date == null && day === today))
          return (
            <div
              key={day}
              className={'relative border-b border-l border-slate-100 p-1 ' + (inMonth ? '' : 'bg-slate-50/60')}
              onMouseDown={(e) => {
                if (e.target === e.currentTarget) onCreate(day)
              }}
            >
              <div className={'mb-1 text-right text-xs ' + (day === today ? 'font-bold text-[var(--accent)]' : 'text-slate-400')}>{dayNum(day)}</div>
              <div className="flex flex-col gap-0.5">
                {dayTodos.slice(0, 4).map((t) => {
                  const accent = t.client_id != null ? clientById.get(t.client_id)?.accent_color ?? '#64748b' : '#cbd5e1'
                  return (
                    <button
                      key={t.id}
                      onMouseDown={(e) => startDrag(e, t.id)}
                      className={'flex items-center gap-1 truncate rounded px-1 py-0.5 text-left text-[11px] ' + (selId === t.id ? 'ring-1 ring-slate-800' : '')}
                      style={{ backgroundColor: withAlpha(accent, '22') }}
                    >
                      <span className="h-1.5 w-1.5 shrink-0 rounded-full" style={{ backgroundColor: accent }} />
                      <span className="truncate text-slate-700">{t.task || '(untitled)'}</span>
                    </button>
                  )
                })}
                {dayTodos.length > 4 && <span className="px-1 text-[10px] text-slate-400">+{dayTodos.length - 4} more</span>}
              </div>
            </div>
          )
        })}
      </div>
      <p className="border-t border-slate-100 px-2 py-1 text-center text-xs text-slate-400">Click a day to add · drag a chip to another day to reschedule.</p>
    </div>
  )
}

// ── Sidebar edit form ────────────────────────────────────────────────────────
function TodoSidePanel({
  sel,
  todos,
  clients,
  allCats,
  dailyStart,
  onPatch,
  onDelete,
  onLog,
  onClose,
}: {
  sel: number | null
  todos: Todo[]
  clients: Client[]
  allCats: Category[]
  dailyStart: number
  onPatch: (id: number, p: Partial<Todo>) => void
  onDelete: (id: number) => void
  onLog: (t: Todo) => void
  onClose: () => void
}) {
  const todo = sel != null ? todos.find((t) => t.id === sel) : undefined
  if (!todo) {
    return <div className="rounded-lg border border-dashed border-slate-300 p-6 text-center text-sm text-slate-400">Drag on the calendar to add a to-do, or click one to edit it.</div>
  }
  const cats = todo.client_id != null ? allCats.filter((c) => c.client_id === todo.client_id) : []
  const startMin = todo.start_time ? hhmmToMin(todo.start_time) : dailyStart
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="font-semibold text-slate-900">To-do</h3>
        <button className="text-slate-400 hover:text-slate-700" onClick={onClose}>
          ✕
        </button>
      </div>
      <Field label="Task">
        <input className={fieldCls} value={todo.task} onChange={(e) => onPatch(todo.id, { task: e.target.value })} />
      </Field>
      <Field label="Client">
        <div className="rounded-md border border-slate-300 px-1">
          <ClientCombo value={todo.client_id} clients={clients} onCommit={(id) => onPatch(todo.id, { client_id: id, category_id: null })} gridKeyDown={() => {}} registerRef={() => {}} />
        </div>
      </Field>
      <Field label="Category">
        <div className={'rounded-md border border-slate-300 px-1 ' + (todo.client_id == null ? 'bg-slate-50' : '')}>
          <CategoryCombo value={todo.category_id} categories={cats} disabled={todo.client_id == null} onCommit={(id) => onPatch(todo.id, { category_id: id })} gridKeyDown={() => {}} registerRef={() => {}} />
        </div>
      </Field>
      <div className="grid grid-cols-3 gap-2">
        <Field label="Date">
          <input type="date" className={fieldCls + ' tabular'} value={todo.due_date ?? ''} onChange={(e) => onPatch(todo.id, { due_date: e.target.value || null })} />
        </Field>
        <Field label="Start">
          <input type="time" className={fieldCls + ' tabular'} value={todo.start_time ?? minToHHMM(startMin)} onChange={(e) => onPatch(todo.id, { start_time: e.target.value || null })} />
        </Field>
        <Field label="Dur (h)">
          <DurInput minutes={todo.est_minutes} onCommit={(m) => onPatch(todo.id, { est_minutes: m })} />
        </Field>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <Field label="Urgency">
          <Scale value={todo.urgency} onChange={(v) => onPatch(todo.id, { urgency: v })} />
        </Field>
        <Field label="Impact">
          <Scale value={todo.impact} onChange={(v) => onPatch(todo.id, { impact: v })} />
        </Field>
      </div>
      <div className="mt-3 flex items-center justify-between">
        <button className="text-sm text-red-600 hover:underline" onClick={() => onDelete(todo.id)}>
          Delete
        </button>
        <div className="flex items-center gap-3">
          <button className="rounded bg-green-100 px-2.5 py-1 text-xs font-medium text-green-800 hover:bg-green-200" onClick={() => onLog(todo)}>
            Log / complete
          </button>
          <button className="text-sm text-slate-500 hover:underline" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  )
}

const fieldCls = 'w-full rounded-md border border-slate-300 bg-white px-2 py-1.5 text-sm text-slate-900 focus:border-[var(--accent)]'
function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="mb-3 block">
      <span className="mb-1 block text-xs font-medium text-slate-600">{label}</span>
      {children}
    </label>
  )
}
function Scale({ value, onChange }: { value: number | null; onChange: (v: number | null) => void }) {
  return (
    <select className={fieldCls + ' tabular'} value={value ?? ''} onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}>
      <option value="">—</option>
      {[1, 2, 3, 4, 5].map((n) => (
        <option key={n} value={n}>
          {n}
        </option>
      ))}
    </select>
  )
}
function DurInput({ minutes, onCommit }: { minutes: number | null; onCommit: (m: number | null) => void }) {
  const [text, setText] = useState(() => (minutes ? formatHours(minutes) : ''))
  useEffect(() => setText(minutes ? formatHours(minutes) : ''), [minutes])
  return (
    <input
      className={fieldCls + ' tabular'}
      value={text}
      placeholder="1"
      onChange={(e) => setText(e.target.value)}
      onBlur={() => {
        const t = text.trim()
        if (t === '') onCommit(null)
        else {
          const p = parseDuration(t)
          if (p != null && p !== minutes) onCommit(p)
          else setText(minutes ? formatHours(minutes) : '')
        }
      }}
    />
  )
}
